// Third-party
import React from 'react';
import classNames from 'classnames';
import round from 'lodash/round';
import uniqueId from 'lodash/uniqueId';

// Components
import { IconButton } from '@/views/components/buttons';
import { SelectInput, SelectOption, UnitInput } from '@/views/components/inputs';
import { Tooltip, DisclaimerTooltip } from '@/views/components/tooltips';
import { CurrencyLabel } from '@/views/components/misc';
import ShelfStatus from '@/views/components/shelf-management/ShelfStatus';

// Enums
import { HoldingAllocationMode } from '@/enums/Portfolio';

// Types
import Disclaimer from '@/types/disclaimer';
import LocalizedString from '@/types/localized-string';
import ThemeMode from '@/types/theme-mode';

// Utils
import { FormattedMessage, formatLocalizedString, formatMessage } from '@/utils/i18n';
import { DISCLAIMER_KEYS } from '@/utils/disclaimers/constants';

type Props = {
  className?: string;
  compact?: boolean;
  currency?: string;
  name?: LocalizedString;
  symbols?: string[];
  symbol?: string | null;
  weight: number;
  dollars?: number;
  units?: number | null;
  allocationMode: HoldingAllocationMode;
  onSymbolChange: (symbol: string) => void;
  onWeightChange: (weight: number) => void;
  onDollarsChange: (dollars: number) => void;
  onUnitsChange: (units: number) => void;
  onRemoveHolding: () => void;
  dark?: boolean;
  theme?: ThemeMode;
  additionalColumns?: JSX.Element;
  holdingDisclaimer?: Disclaimer;
  showDollars?: boolean;
  readOnly?: boolean;
  showCurrencies?: boolean;
  showDelete: boolean;
  weightDisabled?: boolean;
  weightDisabledTooltip?: string;
  unitsIsDisabled?: boolean;
  flags?: string[];
  onChangeHoldingMissing: () => void;
  selected?: boolean;
  shelfName?: string;
};

class GenericHolding extends React.Component<Props> {
  containerRef = React.createRef<HTMLDivElement>();

  uniqueId: string;

  static defaultProps = {
    allocationMode: HoldingAllocationMode.Weight,
    showDelete: true,
    onDollarsChange: () => {},
    onSymbolChange: () => {},
    onWeightChange: () => {},
    onRemoveHolding: () => {},
    onUnitsChange: () => {},
    onChangeHoldingMissing: () => {},
    selected: false,
  };

  constructor(props: Props) {
    super(props);
    this.uniqueId = uniqueId('generic-holding-');
  }

  formatAllocation = (value?: number) => `${round(value || 0, 2)}%`;

  onHoldingClick = () => {
    const isMissingHolding = this.isMissingHolding();
    if (!isMissingHolding || !this.props.onChangeHoldingMissing) {
      return;
    }

    this.props.onChangeHoldingMissing();
  };

  isMissingHolding = () => {
    const { holdingDisclaimer } = this.props;
    return holdingDisclaimer && holdingDisclaimer.key === DISCLAIMER_KEYS.EDIT_PORTFOLIO_HOLDING_MISSING;
  };

  renderHoldingSymbol = () => {
    const { dark, theme, symbol, symbols, onSymbolChange, readOnly } = this.props;
    if (symbol) {
      if (symbols && symbols.length > 1 && !readOnly) {
        return (
          <p className="generic-holding-symbols">
            <SelectInput className={classNames({ dark, theme })} onValueChange={onSymbolChange} selected={symbol}>
              {symbols.map(s => (
                <SelectOption key={s} value={s}>
                  {s}
                </SelectOption>
              ))}
            </SelectInput>
          </p>
        );
      }

      return (
        <p className="generic-holding-symbols generic-holding-symbol">
          <span>{symbol}</span>
        </p>
      );
    }

    return null;
  };

  render() {
    const {
      dollars,
      weight,
      units,
      dark,
      theme,
      holdingDisclaimer,
      additionalColumns,
      showDollars,
      weightDisabled,
      readOnly,
      showCurrencies,
      currency,
      name,
      className,
      showDelete,
      weightDisabledTooltip,
      allocationMode,
      unitsIsDisabled,
      flags,
      shelfName,
      compact = false,
    } = this.props;

    const holdingName = name ? formatLocalizedString(name) : undefined;
    const isMissingHolding = this.isMissingHolding();
    const disableWeights = weightDisabled || readOnly || isMissingHolding;
    const disableUnits = unitsIsDisabled || readOnly || isMissingHolding;

    const classes = classNames('generic-holding d-flex align-items-center', className, {
      dark,
      [`${theme}`]: theme,
      'missing-holding': isMissingHolding,
      'missing-holding-selected': this.props.selected,
    });

    return (
      <div ref={this.containerRef} className={classes} onClick={this.onHoldingClick}>
        <div className="generic-holding-field generic-holding-info column holding">
          <div className="generic-holding-top-infos">
            {showCurrencies && currency && (
              <div>
                <CurrencyLabel currency={currency} />
              </div>
            )}
            {this.renderHoldingSymbol()}
          </div>

          <div className="generic-holding-name d-flex">
            {holdingDisclaimer && <DisclaimerTooltip disclaimer={holdingDisclaimer} />}
            {holdingName}
            {flags && <ShelfStatus isV2={theme === ThemeMode.LIGHT} statuses={flags} tooltipClassName="custom-tooltip-v2" title={shelfName} />}
          </div>
        </div>

        {!compact && (
          <>
            {additionalColumns}

            {allocationMode === HoldingAllocationMode.Weight && (
              <div className="generic-holding-field column weight">
                <label className="field-label weight" htmlFor={`${this.uniqueId}-weight`}>
                  <FormattedMessage id="HoldingsEditor.Weight" />
                </label>

                <Tooltip message={weightDisabledTooltip} disabled={!disableWeights || !weightDisabledTooltip || readOnly}>
                  <div>
                    <UnitInput
                      id={`${this.uniqueId}-weight`}
                      type="number"
                      unitSymbol="%"
                      dark={dark}
                      theme={theme}
                      disabled={disableWeights || readOnly}
                      min={0}
                      value={weight || ''}
                      onValueChange={(value: string) => this.props.onWeightChange(Number(value))}
                      onClick={event => event.stopPropagation()}
                      controlled={false}
                    />
                  </div>
                </Tooltip>
              </div>
            )}

            {allocationMode === HoldingAllocationMode.Units && (
              <div className="generic-holding-field column units">
                <label className="field-label units" htmlFor={`${this.uniqueId}-units`}>
                  <FormattedMessage id="HoldingsEditor.AllocationMode.Units" />
                </label>

                <Tooltip message={formatMessage('HoldingsEditor.UnitsDisabled')} disabled={!disableUnits || readOnly}>
                  <div>
                    <UnitInput
                      id={`${this.uniqueId}-units`}
                      type="number"
                      dark={dark}
                      theme={theme}
                      disabled={disableUnits || readOnly}
                      min={0}
                      value={!isMissingHolding && units ? units.toString() : ''}
                      onValueChange={(value: string) => this.props.onUnitsChange(Number(value))}
                      onClick={event => event.stopPropagation()}
                      controlled={false}
                    />
                  </div>
                </Tooltip>
              </div>
            )}

            {showDollars && (
              <div className="generic-holding-field generic-holding-dollars column dollars">
                <label className="field-label" htmlFor={`${this.uniqueId}-dollars`}>
                  <FormattedMessage id="HoldingsEditor.Dollars" />
                </label>

                <UnitInput
                  id={`${this.uniqueId}-dollars`}
                  type="number"
                  dark={dark}
                  theme={theme}
                  min={0}
                  onValueChange={(value: string) => this.props.onDollarsChange(Number(value))}
                  value={dollars || ''}
                  unitSymbol="$"
                  disabled={readOnly || isMissingHolding}
                  controlled={false}
                />
              </div>
            )}

            {!readOnly && showDelete && (
              <div className="generic-holding-field column delete">
                <IconButton
                  dark={dark}
                  theme={theme}
                  icon={theme === ThemeMode.DEFAULT || theme === ThemeMode.LIGHT ? 'remove-new' : 'ds-delete'}
                  onClick={this.props.onRemoveHolding}
                  aria-label={formatMessage('Common.Remove')}
                />
              </div>
            )}
          </>
        )}
      </div>
    );
  }
}

export default GenericHolding;
